<script setup lang="ts">
import type { UserNotificationHydraItem } from "@verbleif/lib/src/composables/Api/Type/UserNotification/userNotification.interface";
import VButton from "@verbleif/lib/src/components/Button/VButton.vue";
import DialogFooter from "@verbleif/lib/src/components/Dialog/DialogFooter.vue";
import DialogTitle from "@verbleif/lib/src/components/Dialog/DialogTitle.vue";
import VDivider from "@verbleif/lib/src/components/Divider/VDivider.vue";
import VIcon from "@verbleif/lib/src/components/Icon/VIcon.vue";
import { tryUseDialogLayer } from "@verbleif/lib/src/composables/Dialog/useDialogLayer";
import { getQueryClient } from "@verbleif/lib/src/composables/Query/queryClient";
import moment from "moment";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { toast } from "vue-sonner";
import { useAuthStore } from "../../AuthStore/useAuthStore";
import { useServerErrorHandler } from "../../Error/serverErrorHandler";
import { useAppMutation } from "../../Mutation/useHydraMutations";
import { useResourceScope } from "../../Query/resourceKeys";
import { truncateText } from "../../truncateText";
import { inboxNotificationRoute, parseInboxNotificationLink } from "./inboxNotificationLink";
import {
  flushQuietHoursSummaryMutation,
  markNotificationAsReadMutation,
  quietHoursSummaryQueryOptions,
} from "./notifications.api";
import { useUserNotificationsLive } from "./useUserNotificationsLive";

const props = defineProps<{
  channel: number
  windowEndsAt: string
}>();

const emit = defineEmits<{
  close: []
}>();

const layer = tryUseDialogLayer();

const TRANSLATION_KEY = "settings.notifications.v2.quiet_hours_summary_modal";

const { t } = useI18n();
const router = useRouter();
const { user } = useAuthStore();
const { onError } = useServerErrorHandler();
const resourceScope = useResourceScope();
const flushSummaryMutation = useAppMutation(flushQuietHoursSummaryMutation());
const markReadMutation = useAppMutation(markNotificationAsReadMutation());
const { refreshCountUnlessLive } = useUserNotificationsLive();
const flushing = ref(false);

const items = ref<UserNotificationHydraItem[]>([]);
const loading = ref(false);
const hasError = ref(false);

const hasItems = computed(() => items.value.length > 0);

const formattedWindowEnd = computed(() => {
  if (!props.windowEndsAt) {
    return "";
  }
  return moment(props.windowEndsAt).format("MMM D, HH:mm");
});

async function load() {
  const userId = user.value?.id;
  if (!userId) {
    return;
  }

  loading.value = true;
  hasError.value = false;
  try {
    items.value = await getQueryClient().fetchQuery(
      quietHoursSummaryQueryOptions(resourceScope.value, userId, {
        channel: props.channel,
        windowEndsAt: props.windowEndsAt,
      }),
    );
  } catch (e) {
    hasError.value = true;
    onError({ error: e });
  } finally {
    loading.value = false;
  }
}

async function handleFlush() {
  const userId = user.value?.id;
  if (!userId) {
    return;
  }

  flushing.value = true;
  try {
    await flushSummaryMutation.mutateAsync(userId);
    toast.success(t(`${TRANSLATION_KEY}.flush_success`));
    emit("close");
    await layer?.close();
  } catch (e) {
    onError({ error: e });
  } finally {
    flushing.value = false;
  }
}

async function openNotification(notification: UserNotificationHydraItem) {
  const target = parseInboxNotificationLink(notification.link);
  if (!target) {
    toast.error(t(`${TRANSLATION_KEY}.no_link`));
    return;
  }

  if (!notification.readAt) {
    const readAt = new Date().toISOString();
    try {
      await markReadMutation.mutateAsync({ notificationId: notification.id, readAt, isV2: true });
      items.value = items.value.map(item => item["@id"] === notification["@id"] ? { ...item, readAt } : item);
      void refreshCountUnlessLive();
    } catch (e) {
      // Opening the item matters more than its read state; the item stays unread in the inbox.
      onError({ error: e, hideToast: true });
    }
  }

  // Close this dialog first: a dialog opened by the route (the task, the report) would otherwise be stacked on top of
  // it and close with it, which drops the item from the route again.
  await layer?.close();
  await router.push(inboxNotificationRoute(target));
}

onMounted(async () => {
  await load();
});
</script>

<template>
  <div class="h-full">
    <div class="flex flex-col text-gray-800 leading-5 dark:text-white h-[600px] w-full flex-1">
      <DialogTitle>
        {{ t(`${TRANSLATION_KEY}.title`) }}
      </DialogTitle>
      <div class="px-5 text-sm text-gray-500 dark:text-gray-400">
        {{ t(`${TRANSLATION_KEY}.subtitle`, { windowEnd: formattedWindowEnd }) }}
      </div>

      <div v-if="hasError" class="flex flex-col items-center justify-center p-8 text-center flex-1 overflow-hidden">
        <VIcon name="triangle-alert" class="text-4xl text-red-500 mb-4" />
        <div class="text-gray-600 dark:text-gray-400">
          {{ t(`${TRANSLATION_KEY}.error_loading`) }}
        </div>
        <VButton variant="secondary" class="mt-4" @click="load">
          {{ t(`${TRANSLATION_KEY}.try_again`) }}
        </VButton>
      </div>

      <div v-else-if="loading" class="flex flex-1 overflow-hidden">
        <ul class="flex flex-col w-full max-h-[500px] overflow-y-auto mt-3">
          <li v-for="n in 5" :key="`summary-skeleton-${n}`">
            <div class="flex flex-col">
              <div class="mx-5">
                <VDivider no-margins />
              </div>
              <div class="flex pr-3 py-3 rounded-lg group">
                <div class="flex h-4 w-5 items-center justify-center" />
                <div class="flex flex-col w-full gap-1.5">
                  <div class="flex justify-between">
                    <VSkeleton :width="160" :height="14" :dynamic-width="false" width-unit="px" height-unit="px" />
                    <VSkeleton :width="40" :height="11" :dynamic-width="false" width-unit="px" height-unit="px" />
                  </div>
                  <VSkeleton :width="280" :height="12" width-unit="px" height-unit="px" />
                </div>
              </div>
            </div>
          </li>
        </ul>
      </div>

      <div
        v-else-if="!hasItems"
        class="flex flex-col items-center justify-center p-8 text-center flex-1 overflow-hidden"
      >
        <VIcon name="inbox" class="text-4xl text-gray-400 mb-4" />
        <div class="text-gray-600 dark:text-gray-400">
          {{ t(`${TRANSLATION_KEY}.empty`) }}
        </div>
      </div>

      <div v-else class="flex flex-1 overflow-hidden">
        <ul class="flex flex-col w-full max-h-[500px] overflow-y-auto mt-3 text-gray-800 dark:text-white">
          <li
            v-for="notification in items"
            :key="notification.id"
            class="hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <div class="flex flex-col">
              <div class="mx-5">
                <VDivider no-margins />
              </div>
              <div
                class="flex pr-3 py-3 rounded-lg group cursor-pointer"
                @click="openNotification(notification)"
              >
                <div class="flex h-4 w-5 items-center justify-center">
                  <div v-if="!notification.readAt" class="w-2 h-2 bg-primary-600 rounded-full" />
                </div>
                <div class="flex flex-col w-full">
                  <div class="flex justify-between">
                    <div :class="notification.readAt ? '' : 'font-bold'" class="dark:text-white text-sm leading-4 mb-1">
                      {{ notification.title }}
                    </div>
                    <div v-if="notification.listedAt ?? notification.createdAt" class="text-gray-400 dark:text-grey-200 text-xs">
                      {{ moment(notification.listedAt ?? notification.createdAt).format('HH:mm') }}
                    </div>
                  </div>
                  <div class="text-gray-500 font-normal leading-4 dark:text-grey-300 w-3/4 text-sm">
                    {{ truncateText(notification.message ?? '', 80) }}
                  </div>
                </div>
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>
    <DialogFooter>
      <VButton rounded variant="gray" @click="layer?.close()">
        {{ t('base.close') }}
      </VButton>
      <VButton
        rounded
        variant="primary"
        icon-left="mail-open"
        :loading="flushing"
        :disabled="!hasItems"
        @click="handleFlush"
      >
        {{ t(`${TRANSLATION_KEY}.flush_action`) }}
      </VButton>
    </DialogFooter>
  </div>
</template>
